/* eslint-disable no-useless-catch */
import sortBy from 'lodash/sortBy'

import { useAppContext } from 'context'
import { Bundle } from 'types/bundle'
import { ContentBlock } from 'types/contentful'

import query from '../query'

export const convertDurationString = (str: string) => {
  const arr = str.split(':')
  const hours = Number(arr[0])
  const minutes = Number(arr[1])
  const seconds = Number(arr[2])
  return `${hours > 0 ? `${hours} hours` : ''}${minutes} mins ${
    seconds < 10 ? `0${seconds}` : `${seconds}`
  } sec`
}

/**
 * A wrapper around `fetch` that re-tries if it fails
 *
 *
 * @param url URL to fetch
 * @param retries number of retries to refresh the token
 * @category HTTP
 */
export async function repeatableFetch(
  url: string,
  retries: number = 1
): ReturnType<typeof fetch> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${process.env.APP_CONTENTFUL_DELIVERY_TOKEN}`,
    },
    body: JSON.stringify({
      query,
    }),
  })

  if (response.status === 401 && retries > 0) {
    // Try again
    return repeatableFetch(url, retries - 1)
  }

  return response
}

export function useBundleData() {
  const { bundleLoader } = useAppContext()

  const getHomeData = () => {
    if (!bundleLoader) return null
    const { homeBanner, sections } = bundleLoader.data
    return {
      homeBanner: homeBanner.items[0],
      sections: sortBy(sections.items, ['order']),
    }
  }

  const getEssayItem = (id: string) => {
    if (!bundleLoader) return null
    const { essayItems } = bundleLoader.data
    const essayItem = essayItems.items.find(({ sys }) => sys.id === id)
    if (!essayItem) return null
    const { title, description, image, sys, largeImage } = essayItem
    return {
      id: sys.id,
      title,
      description,
      image,
      largeImage,
    }
  }

  const getEssayDetails = (id: string) => {
    if (!bundleLoader) return null
    const { essays } = bundleLoader.data
    const essay = essays.items.find(({ sys }) => sys.id === id)
    if (!essay) return null
    const {
      sys,
      title,
      subtitle,
      coverImage,
      coverImageLarge,
      image,
      largeImage,
      description,
      essayItemsCollection,
      thumbnail,
    } = essay
    return {
      id: sys.id,
      title,
      subtitle,
      coverImage,
      coverImageLarge,
      image,
      largeImage,
      description,
      essayItemsCollection,
      thumbnail,
    }
  }

  const getEssay = (id: string) => {
    const essay = getEssayDetails(id)
    if (!essay) return null
    const {
      title,
      subtitle,
      coverImage,
      coverImageLarge,
      description,
      essayItemsCollection,
    } = essay
    const items = essayItemsCollection?.items
      .map((item) => (item?.sys.id ? getEssayItem(item.sys.id) : null))
      .filter((item) => item !== null)
    return {
      title,
      subtitle,
      coverImage,
      coverImageLarge,
      description,
      essayItemsCollection: items,
    }
  }

  const getAudioStory = (storySlug: string) => {
    if (!bundleLoader) return null
    const { audioStories } = bundleLoader.data
    const sortedStories = sortBy(audioStories.items, ['order'])
    const storyIndex = sortedStories.findIndex(({ slug }) => slug === storySlug)
    const nextStoryIndex =
      storyIndex + 1 >= sortedStories.length ? 0 : storyIndex + 1

    const nextStory = sortedStories[nextStoryIndex]

    const story = sortedStories[storyIndex]
    if (!story) return null
    const {
      title,
      image,
      largeImage,
      description,
      credits,
      related,
      duration,
      audiosCollection,
      sys: { id },
      slug,
    } = story
    const relatedItem = related?.sys.id
      ? getEssayDetails(related?.sys.id)
      : null
    return {
      id,
      slug,
      title,
      image,
      largeImage,
      description,
      duration: duration ? convertDurationString(duration) : '',
      credits,
      audiosCollection,
      nextStory: {
        slug: nextStory.slug,
        title: nextStory.title,
        duration: nextStory.duration
          ? convertDurationString(nextStory.duration)
          : '',
      },
      related: relatedItem,
    }
  }

  const getAboutDetail = (aboutSlug: string) => {
    if (!bundleLoader) return null
    const { about } = bundleLoader.data
    const section = about.items.find(({ slug }) => slug === aboutSlug)
    if (!section) return null
    const {
      title,
      content,
      order,
      slug,
      sys: { id },
    } = section
    const contentBlocks =
      content?.links.entries.block.filter(
        (block): block is ContentBlock => !!block
      ) || []
    return {
      title,
      content,
      contentBlocks,
      order,
      slug,
      id,
    }
  }

  const getAboutNav = () => {
    if (!bundleLoader) return null
    const { about } = bundleLoader.data
    return sortBy(about.items, ['order']).map(({ title, slug }) => ({
      title,
      slug,
    }))
  }

  const getHeroesDetail = () => {
    if (!bundleLoader) return null
    const { heroesDetail } = bundleLoader.data
    return heroesDetail.items[0]
  }

  const listHeroes = () => {
    if (!bundleLoader) return null
    const { heroes } = bundleLoader.data
    return sortBy(heroes.items, ['lastName', 'firstName']).map(
      ({
        firstName,
        lastName,
        role,
        sys: { id },
        image,
        slug,
        largeImage,
        description,
        filtersCollection,
      }) => ({
        firstName,
        lastName,
        role,
        id,
        slug,
        image,
        largeImage,
        description,
        filtersCollection: filtersCollection?.items
          .map((filter) => {
            if (!filter?.slug || !filter?.title) return undefined
            return {
              slug: filter.slug,
              title: filter.title,
            }
          })
          .filter(
            (
              item
            ): item is {
              slug: string
              title: string
            } => !!item
          ),
      })
    )
  }

  const listFilters = () => {
    if (!bundleLoader) return null
    const { filters } = bundleLoader.data
    return sortBy(filters.items, ['title']).map(({ slug, title }) => ({
      slug,
      title,
    }))
  }

  const listAudioStories = () => {
    if (!bundleLoader) return null
    const { audioStories } = bundleLoader.data
    return sortBy(audioStories.items, ['order']).map(
      ({
        sys: { id },
        slug,
        title,
        image,
        largeImage,
        description,
        credits,
        duration,
        related,
      }) => ({
        id,
        slug,
        title,
        image,
        largeImage,
        description,
        credits,
        duration: duration ? convertDurationString(duration) : '',
        related,
      })
    )
  }

  const listEssays = () => {
    if (!bundleLoader) return null
    const { essays } = bundleLoader.data
    return sortBy(essays.items, ['order']).map(
      ({
        title,
        subtitle,
        image,
        largeImage,
        description,
        essayItemsCollection,
        thumbnail,
        sys: { id },
      }) => ({
        id,
        title,
        subtitle,
        image,
        largeImage,
        description,
        thumbnail,
        essayCount: essayItemsCollection?.items.length || 0,
        essayItemsCollection,
      })
    )
  }

  return {
    getHomeData,
    getEssay,
    getAudioStory,
    listHeroes,
    listAudioStories,
    listEssays,
    listFilters,
    getAboutNav,
    getAboutDetail,
    getHeroesDetail,
  }
}

export function useBundle() {
  /**
   * Wrapper around fetch() that does the following things:
   *
   * - checks the response
   * - updates the state (`data`)
   *
   * @param url URL to fetch
   * @param payload request body
   */
  const fetchAndParse = async (url: string): Promise<any> => {
    try {
      const response = await repeatableFetch(url)

      if (!response.ok) {
        const json = await response.json()
        throw json || { code: response.status, detail: response.statusText }
      }

      const json = await response.json()

      return json
    } catch (exc) {
      throw exc
    }
  }

  const fullBundle = async () => {
    const domain = process.env.APP_CONTENT_DOMAIN
    const spaceId = process.env.APP_CONTENTFUL_SPACE_ID

    if (!domain) throw new Error('APP_CONTENT_DOMAIN missing from .env')
    if (!spaceId) throw new Error('APP_CONTENTFUL_SPACE_ID missing from .env')

    const CONTENTFUL = `${domain}${spaceId}`

    try {
      const contentfulBundle: Bundle = await fetchAndParse(CONTENTFUL)
      if (contentfulBundle) {
        try {
          console.log('contentfulBundle', contentfulBundle)
          return contentfulBundle
        } catch (e) {
          throw e
        }
      } else {
        throw new Error('Content is empty')
      }
    } catch (e) {
      throw e
    }
  }

  return {
    fullBundle,
  }
}
